import React, { useEffect, useState } from "react";
import { Download, Share, X, Smartphone } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

/** Mobile-only "Install this app on your phone" prompt.
 *
 *  Android/Chrome: uses the `beforeinstallprompt` event → one-tap native install.
 *  iOS/Safari: no such event exists → shows step-by-step Add-to-Home-Screen instructions.
 *  Hidden once already installed (standalone mode) or once the visitor dismisses it.
 */
const DISMISS_KEY = "icc_install_prompt_dismissed_v1";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS uses this legacy prop
    window.navigator.standalone === true
  );
};

const InstallAppPrompt = () => {
  const { t } = useLanguage();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Note: responsive visibility is handled by `sm:hidden` on the DOM
    // element — no need to gate here. That way the beforeinstallprompt event
    // is captured even if the visitor rotates or resizes.

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS gets no `beforeinstallprompt`; show the prompt after a small delay
    // so it isn't intrusive.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
          dismiss();
        }
      } catch (e) {
        // Ignore — user closed the native prompt
      }
      setDeferred(null);
      return;
    }
    // iOS path — show the instruction card
    setShowIOSHelp(true);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-app-prompt"
        className="fixed left-3 right-3 bottom-3 z-40 sm:hidden animate-fade-in-up"
        style={{ direction: "ltr" }}
      >
        <div className="rounded-2xl shadow-2xl bg-gradient-to-br from-forest to-[#1F4030] text-sand border border-gold/40 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gold/20 border border-gold/50 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-gold font-semibold mb-0.5">
              {t("install_app_pill")}
            </p>
            <p className="text-sm font-medium leading-snug">{t("install_app_title")}</p>
          </div>
          <button
            type="button"
            onClick={install}
            data-testid="install-app-btn"
            className="bg-gold hover:bg-[#A07A2F] text-ink text-xs font-semibold px-3.5 py-2 rounded-full whitespace-nowrap flex-shrink-0"
          >
            <Download className="w-3.5 h-3.5 inline mr-1" />
            {t("install_app_cta")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="dismiss"
            data-testid="install-app-dismiss"
            className="text-sand/60 hover:text-sand p-1 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS instruction modal */}
      {showIOSHelp && (
        <div
          data-testid="install-app-ios-modal"
          className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={dismiss}
        >
          <div
            className="bg-sand rounded-3xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <Smartphone className="w-10 h-10 text-forest mx-auto mb-2" />
              <h3 className="font-heading text-xl text-forest font-semibold">
                {t("install_ios_title")}
              </h3>
            </div>
            <ol className="space-y-3 text-sm text-ink mb-5">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-forest text-sand flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <span className="flex items-center gap-1.5">
                  {t("install_ios_step1_a")}
                  <Share className="w-4 h-4 text-forest inline" />
                  {t("install_ios_step1_b")}
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-forest text-sand flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <span>{t("install_ios_step2")}</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-forest text-sand flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <span>{t("install_ios_step3")}</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={dismiss}
              className="w-full bg-forest text-sand rounded-full py-3 text-sm font-medium"
            >
              {t("install_ios_close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppPrompt;
